import {NO_NODE_ID} from 'common/src/components/CLCardsMap/CLCardsMap.config';
import {CampaignTreeType} from 'common/src/modules/campaigns/campaigns.types';
import {batchUpdateCards} from 'common/src/modules/campaigns/store/campaignLogic/actions/batchUpdateCards';
import {DisconnectedNodeOutputData, MapNode} from 'common/src/modules/nodes/nodes.types';
import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import toolError from 'common/src/modules/shared/copilot/utils/toolError';
import {CLStore} from 'common/src/redux/store';
import {z} from 'zod';
import {CampaignSetupAgentToolName} from '../../../campaignSetupAgent.enums';
import refreshCampaignIssues from '../../../utils/refreshCampaignIssues';
import buildConnectionMap from './utils/buildConnectionMap';
import duplicateSubtreeRecursive from './utils/duplicateSubtreeRecursive';

const duplicateSubtreeToolSchema = z.object({
  cardId: z.number().describe('The ID of the root card whose subtree should be duplicated'),
});

function duplicateSubtreeTool(store: CLStore) {
  async function execute({
    input: {cardId},
  }: ToolExecuteParams<typeof duplicateSubtreeToolSchema>): Promise<ToolResult> {
    try {
      // Phase 1: Validation
      const state = store.getState();
      const nodes = state.campaignPage.campaignLogic.nodes[CampaignTreeType.Campaign];

      if (!nodes) {
        return toolError(
          `No nodes found for campaign tree type ${CampaignTreeType.Campaign}. Make sure you're in a campaign context.`,
        );
      }

      // Find the card to duplicate
      const rootNode = nodes[cardId];

      if (!rootNode) {
        return toolError(
          `Card with ID ${cardId} not found. Available card IDs: ${Object.keys(nodes).join(
            ', ',
          )}`,
        );
      }

      // Get the next available node ID
      const nextNodeId = state.campaignPage.campaignLogic.nextNodeId;

      if (!nextNodeId) {
        return toolError('Unable to determine next node ID');
      }

      // Phase 2: Subtree Traversal & Duplication
      const duplicationResult = duplicateSubtreeRecursive(rootNode, nextNodeId, nodes);

      const {duplicatedNodes} = duplicationResult;

      if (duplicatedNodes.length === 0) {
        return toolError('No nodes were duplicated');
      }

      // Phase 3: Prepare nodes for initial creation (without outputs)
      // First, add all nodes WITHOUT connections so they exist in the database
      const nodesToAdd = duplicatedNodes.map(({node}) => ({
        ...node,
        inputs: [],
        outputs: [], // No outputs yet - will be added in update phase
      })) as MapNode[];

      // Phase 4: Add all cards to database first
      const addResult = await store.dispatch(
        batchUpdateCards({
          nodesToAdd,
          nodesToUpdate: [],
          campaignTreeType: CampaignTreeType.Campaign,
        }),
      );

      if (!batchUpdateCards.fulfilled.match(addResult)) {
        const errorMessage = addResult.error?.message || 'Unknown error occurred';

        return toolError(`Failed to add duplicated cards: ${errorMessage}`);
      }

      // Phase 5: Rebuild Connections
      // Now that all cards exist, we can safely add connections between them
      const idMap = buildConnectionMap(duplicatedNodes);

      // Update nodes with correct output nodeIds
      const nodesToUpdate: MapNode[] = [];

      for (const {originalId, newId, node} of duplicatedNodes) {
        const originalNode = nodes[originalId];

        if (!originalNode) {
          continue;
        }

        // Skip if no outputs
        if (originalNode.outputs.length === 0) {
          continue;
        }

        // Map each output from the original node, preserving all properties
        // but updating the nodeId to point to the duplicated target
        const updatedOutputs: DisconnectedNodeOutputData[] = originalNode.outputs.map((output) => {
          // If output was disconnected, keep it disconnected
          if (output.nodeId === NO_NODE_ID) {
            return {
              ...output,
              nodeId: NO_NODE_ID,
            };
          }

          // Find the new duplicated node ID for the original target
          const newTargetId = idMap[output.nodeId];

          if (newTargetId !== undefined) {
            // Target was duplicated, connect to the new duplicate
            return {
              ...output,
              nodeId: newTargetId,
            };
          } else {
            // Target was not duplicated (shouldn't happen in subtree duplication)
            // Keep as disconnected
            return {
              ...output,
              nodeId: NO_NODE_ID,
            };
          }
        });

        nodesToUpdate.push({
          ...node,
          id: newId,
          outputs: updatedOutputs,
          inputs: [], // Inputs will be rebuilt by the backend
        } as MapNode);
      }

      // Phase 6: Update cards with connections
      if (nodesToUpdate.length > 0) {
        const updateResult = await store.dispatch(
          batchUpdateCards({
            nodesToAdd: [],
            nodesToUpdate,
            campaignTreeType: CampaignTreeType.Campaign,
          }),
        );

        if (!batchUpdateCards.fulfilled.match(updateResult)) {
          const errorMessage = updateResult.error?.message || 'Unknown error occurred';

          return toolError(`Failed to update connections: ${errorMessage}`);
        }
      }

      // Phase 7: Refresh campaign issues
      await refreshCampaignIssues(store);

      const rootCardIdentifier = rootNode.name || rootNode.description || `ID ${cardId}`;
      const newCardIds = duplicatedNodes.map((d) => d.newId).join(', ');

      return toolResult(
        `Successfully duplicated subtree rooted at card "${rootCardIdentifier}" (ID ${cardId}).\nCreated ${duplicatedNodes.length} new card(s) with IDs: ${newCardIds}.\nThe duplicated subtree has been positioned slightly offset from the original.`,
      );
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';

      return toolError(`Error duplicating subtree: ${errorMessage}`);
    }
  }

  return tool({
    name: CampaignSetupAgentToolName.DuplicateSubtree,
    displayName: 'Duplicating Campaign Subtree',
    description:
      'Duplicates an entire subtree of campaign cards starting from a specified root card. Recursively duplicates all child cards and their connections until reaching creative nodes (leaf nodes) or the end of the flow. The duplicated subtree will maintain all internal connections and be positioned slightly offset from the original.',
    execute,
    inputSchema: duplicateSubtreeToolSchema,
  });
}

export default duplicateSubtreeTool;
